"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BitcoinLocktimeUtils = exports.extractBitcoinRawTxVectors = exports.BitcoinTxHash = void 0;
const bitcoinjs_lib_1 = require("bitcoinjs-lib");
const bufio_1 = __importDefault(require("bufio"));
const ethers_1 = require("ethers");
const utils_1 = require("../utils");
/**
 * Represents a Bitcoin transaction hash (or transaction ID) as an un-prefixed hex
 * string. This hash is supposed to have the same byte order as used by the
 * Bitcoin block explorers which is the opposite of the byte order used
 * by the Bitcoin protocol internally. That means the hash must be reversed in
 * the use cases that expect the Bitcoin internal byte order.
 */
class BitcoinTxHash extends utils_1.Hex {
}
exports.BitcoinTxHash = BitcoinTxHash;
/**
 * Decomposes a transaction in the raw representation into version, vector of
 * inputs, vector of outputs and locktime.
 * @param rawTransaction - Transaction in the raw format.
 * @returns Transaction data with fields represented as un-prefixed hex strings.
 */
function extractBitcoinRawTxVectors(rawTransaction) {
    const toHex = (bufferWriter) => {
        return utils_1.Hex.from(bufferWriter.render());
    };
    const getTxInputVector = (tx) => {
        const buffer = bufio_1.default.write();
        buffer.writeVarint(tx.ins.length);
        tx.ins.forEach((input) => {
            buffer.writeHash(input.hash);
            buffer.writeU32(input.index);
            buffer.writeVarBytes(input.script);
            buffer.writeU32(input.sequence);
        });
        return toHex(buffer);
    };
    const getTxOutputVector = (tx) => {
        const buffer = bufio_1.default.write();
        buffer.writeVarint(tx.outs.length);
        tx.outs.forEach((output) => {
            buffer.writeI64(output.value);
            buffer.writeVarBytes(output.script);
        });
        return toHex(buffer);
    };
    const getTxVersion = (tx) => {
        const buffer = bufio_1.default.write();
        buffer.writeU32(tx.version);
        return toHex(buffer);
    };
    const getTxLocktime = (tx) => {
        const buffer = bufio_1.default.write();
        buffer.writeU32(tx.locktime);
        return toHex(buffer);
    };
    const transaction = bitcoinjs_lib_1.Transaction.fromBuffer(Buffer.from(rawTransaction.transactionHex, "hex"));
    return {
        version: getTxVersion(transaction),
        inputs: getTxInputVector(transaction),
        outputs: getTxOutputVector(transaction),
        locktime: getTxLocktime(transaction),
    };
}
exports.extractBitcoinRawTxVectors = extractBitcoinRawTxVectors;
/**
 * Converts Bitcoin specific locktime value to a number. The number represents
 * either a block height or a Unix timestamp, depending on the value.
 *
 * If the number is less than 500 000 000 it is a block height.
 * If the number is greater or equal 500 000 000 it is a Unix timestamp.
 *
 * @see {@link https://developer.bitcoin.org/devguide/transactions.html#locktime-and-sequence-number Documentation}
 *
 * @param locktimeLE A 4-byte little-endian locktime as an un-prefixed
 *                   hex string {@link: Deposit#refundLocktime}.
 * @returns UNIX timestamp in seconds.
 */
function locktimeToNumber(locktimeLE) {
    const locktimeBE = utils_1.Hex.from(locktimeLE).reverse().toBuffer();
    return ethers_1.BigNumber.from(locktimeBE).toNumber();
}
/**
 * Calculates locktime parameter for the given locktime start timestamp.
 * Throws if the resulting locktime is not a 4-byte number.
 * @param locktimeStartedAt - Unix timestamp in seconds determining the moment
 *        of the locktime start.
 * @param locktimeDuration Locktime duration in seconds.
 * @returns A 4-byte little-endian locktime.
 */
function calculateLocktime(locktimeStartedAt, locktimeDuration) {
    // Locktime is a Unix timestamp in seconds, computed as locktime start
    // timestamp plus locktime duration.
    const locktime = ethers_1.BigNumber.from(locktimeStartedAt + locktimeDuration);
    const locktimeHex = utils_1.Hex.from(locktime.toHexString());
    if (locktimeHex.toString().length != 8) {
        throw new Error("Locktime must be a 4 bytes number");
    }
    // Bitcoin locktime is interpreted as little-endian integer, so we must
    // adhere to that convention by converting the locktime accordingly.
    return locktimeHex.reverse();
}
/**
 * Utility functions allowing to deal with Bitcoin locktime.
 */
exports.BitcoinLocktimeUtils = {
    locktimeToNumber,
    calculateLocktime,
};
//# sourceMappingURL=tx.js.map